'use client';

import { useState, useEffect, useRef } from 'react';
import { ChatWindow }        from './ChatWindow';
import { useVisitorMemory, detectSource } from './useVisitorMemory';
import { useProactiveTrigger, type ProactiveTrigger } from './useProactiveTrigger';

interface ChatLauncherProps {
  locale: string;
}

const DISMISS_KEY = 'zain_note_dismissed';

// ── Page-aware proactive messages (keyed by current path) ─────────────
function pageNote(locale: string, pathname: string): { message: string; cta: string } | null {
  const ar = locale === 'ar';
  const has = (s: string) => pathname.includes(s);

  if (has('/services')) return {
    message: ar ? 'مهتم بخدمة معينة؟ أقدر أعطيك تصوّراً مبدئياً للنطاق.' : 'Curious about a service? I can give a ballpark scope.',
    cta:     ar ? 'اسأل عن مشروعك' : 'Scope my project',
  };
  if (has('/projects') || has('/work')) return {
    message: ar ? 'أعجبك ما رأيت؟ أقدر أشرح لك أي مشروع بالتفصيل.' : 'Like what you see? I can walk you through any project.',
    cta:     ar ? 'اعرف المزيد' : 'Tell me more',
  };
  if (has('/contact')) return {
    message: ar ? 'تفضّل المحادثة أولاً؟ اسألني أي شيء قبل التواصل.' : 'Prefer to chat first? Ask me anything before reaching out.',
    cta:     ar ? 'اسأل زين' : 'Ask Zain',
  };
  if (has('/blog')) return {
    message: ar ? 'استمتعت بالقراءة؟ أقدر أقترح مقالات ذات صلة.' : 'Enjoying the read? I can suggest related posts.',
    cta:     ar ? 'اقترح لي' : 'Suggest posts',
  };
  if (has('/about')) return {
    message: ar ? 'تريد نبذة سريعة عن خبرة محمد؟ اسألني.' : "Want the short version of Mohamed's background? Ask me.",
    cta:     ar ? 'أخبرني' : 'Give me the gist',
  };
  return null;
}

// ── Context-aware proactive messages ─────────────────────────────────
function buildNote(
  locale: string,
  sessionCount: number,
  source: string | null,
  hasSubmittedInquiry: boolean,
  pathname: string,
  trigger: ProactiveTrigger,
): { message: string; cta: string } {
  const ar = locale === 'ar';

  // Returning visitor who already sent an inquiry
  if (hasSubmittedInquiry) {
    return {
      message: ar ? 'رسالتك وصلت! هل تريد إضافة شيء؟'   : 'Your inquiry is with Mohamed. Need to add anything?',
      cta:     ar ? 'تحدث مع زين'                        : 'Talk to Zain',
    };
  }

  // Returning visitor (no inquiry yet)
  if (sessionCount > 1) {
    return {
      message: ar ? 'أهلاً بعودتك! يمكنني مساعدتك أكثر' : 'Welcome back! Let\'s pick up where we left off.',
      cta:     ar ? 'أكمل المحادثة'                      : 'Continue chat',
    };
  }

  // Exit intent — last-chance nudge
  if (trigger === 'exit') {
    return {
      message: ar ? 'قبل أن تغادر — سؤال سريع لزين؟' : 'Before you go — a quick question for Zain?',
      cta:     ar ? 'اسأل بسرعة' : 'Ask quickly',
    };
  }

  // Page-aware message (dwell)
  const page = pageNote(locale, pathname);
  if (page) return page;

  // First-visit source-aware messages
  const sourceMessages: Record<string, { en: { message: string; cta: string }; ar: { message: string; cta: string } }> = {
    linkedin: {
      en: { message: 'Looking to hire a developer? I can help.', cta: 'Let\'s talk' },
      ar: { message: 'تبحث عن مطور؟ يمكنني مساعدتك.',           cta: 'تحدث معي'   },
    },
    github: {
      en: { message: 'Checking out Mohamed\'s work? Ask me anything.', cta: 'Ask Zain' },
      ar: { message: 'تستعرض أعمال محمد؟ اسألني أي شيء.',             cta: 'اسأل زين' },
    },
    google: {
      en: { message: 'Searching for a web developer? You found one.', cta: 'See what I can do' },
      ar: { message: 'تبحث عن مطور ويب؟ وجدته.',                       cta: 'اكتشف ما أقدمه'   },
    },
    upwork: {
      en: { message: 'Need a freelancer? Let\'s discuss your project.', cta: 'Start here' },
      ar: { message: 'تحتاج مستقلاً? لنناقش مشروعك.',                  cta: 'ابدأ هنا'          },
    },
    freelancer: {
      en: { message: 'Need a freelancer? Let\'s discuss your project.', cta: 'Start here' },
      ar: { message: 'تحتاج مستقلاً? لنناقش مشروعك.',                  cta: 'ابدأ هنا'          },
    },
    twitter: {
      en: { message: 'Saw Mohamed on Twitter? Let\'s connect.', cta: 'Say hello' },
      ar: { message: 'رأيت محمد على تويتر؟ لنتواصل.',            cta: 'قل مرحباً'  },
    },
  };

  if (source && sourceMessages[source]) {
    const m = sourceMessages[source];
    return ar ? m.ar : m.en;
  }

  // Default first-visit
  return ar
    ? { message: 'تحتاج مساعدة؟ أنا هنا!', cta: 'تحدث معي' }
    : { message: 'Need help? I\'m Zain, Mohamed\'s assistant.', cta: 'Chat with me' };
}

export function ChatLauncher({ locale }: ChatLauncherProps) {
  const [open,       setOpen]       = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showBadge,  setShowBadge]  = useState(false);
  const [showNote,   setShowNote]   = useState(false);
  const [note,       setNote]       = useState({ message: '', cta: '' });

  const launcherRef = useRef<HTMLButtonElement>(null);
  const ar          = locale === 'ar';
  const { read }    = useVisitorMemory();

  // Visitor dismissed the note earlier this tab session — stay quiet.
  const dismissed = typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1';

  // Behaviour-based trigger (dwell on high-intent pages, or exit intent).
  const { trigger, pathname } = useProactiveTrigger(!hasGreeted && !open && !dismissed);

  // When a trigger fires, build a context- + page-aware note and reveal it.
  useEffect(() => {
    if (!trigger || hasGreeted || open || dismissed) return;
    const v   = read();
    const src = v.source || detectSource();
    setNote(buildNote(locale, v.sessionCount, src, v.hasSubmittedInquiry, pathname, trigger));
    setShowNote(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, pathname, locale]);

  // Show unread badge after 4 s
  useEffect(() => {
    if (hasGreeted || open || dismissed) return;
    const id = setTimeout(() => setShowBadge(true), 4000);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGreeted, open]);

  // Let fixed corner widgets (e.g. ScrollToTop) know the panel is covering them.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('zain:state', { detail: { open } }));
  }, [open]);

  // Return focus to launcher when chat closes
  useEffect(() => {
    if (!open) launcherRef.current?.focus();
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Allow other parts of the app (e.g. the "Meet Zain" home section) to open the chat.
  useEffect(() => {
    const openChat = () => {
      setOpen(true);
      setShowBadge(false);
      setShowNote(false);
      setHasGreeted(true);
    };
    window.addEventListener('zain:open', openChat);
    return () => window.removeEventListener('zain:open', openChat);
  }, []);

  const toggle = () => {
    setOpen((o) => !o);
    setShowBadge(false);
    setShowNote(false);
    setHasGreeted(true);
  };

  const dismissNote = () => {
    setShowNote(false);
    setHasGreeted(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div className="fixed bottom-6 inset-e-6 z-50 flex flex-col items-end gap-3">
      {/* Context-aware proactive note */}
      {showNote && !open && (
        <div
          role="alert"
          dir={ar ? 'rtl' : 'ltr'}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-xl
            bg-(--bg) border border-(--border) shadow-lg
            animate-fade-in max-w-xs
          `}
        >
          <p className="text-sm text-(--text) flex-1 leading-snug">{note.message}</p>
          <button
            onClick={toggle}
            className="text-xs font-semibold text-primary-500 hover:text-primary-600 transition-colors cursor-pointer shrink-0"
          >
            {note.cta}
          </button>
          <button
            onClick={dismissNote}
            aria-label="Dismiss"
            className="w-5 h-5 rounded flex items-center justify-center text-(--muted) hover:text-(--text) transition-colors cursor-pointer shrink-0"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Chat window */}
      {open && <ChatWindow locale={locale} onClose={() => setOpen(false)} />}

      {/* Launcher button */}
      <button
        ref={launcherRef}
        onClick={toggle}
        aria-label={open ? 'Close chat' : 'Open chat with Zain, AI assistant'}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{ width: '52px', height: '52px' }}
        className="
          relative rounded-full
          bg-primary-500 hover:bg-primary-600 active:bg-primary-700
          text-white shadow-lg hover:shadow-xl
          flex items-center justify-center
          transition-all duration-180 cursor-pointer
          focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2
        "
      >
        {showBadge && !open && (
          <span
            aria-label="New message"
            role="status"
            className="absolute -top-1 -inset-e-1 w-4 h-4 rounded-full bg-secondary-400 border-2 border-(--bg) animate-scale-in"
          />
        )}
        {open ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default ChatLauncher;
